// Role badge colors, keyed by role name (slug). Roles are admin-creatable,
// so unknown names fall back to a deterministic color from ROLE_FALLBACK_COLORS
// rather than a single generic gray for every custom role.
const ROLE_BADGE_COLORS = {
  admin: "bg-blue-50 text-blue-700",
  commercial: "bg-emerald-50 text-emerald-700",
  onboarding: "bg-amber-50 text-amber-700",
};

const ROLE_FALLBACK_COLORS = [
  "bg-violet-50 text-violet-700",
  "bg-cyan-50 text-cyan-700",
  "bg-pink-50 text-pink-700",
  "bg-indigo-50 text-indigo-700",
  "bg-orange-50 text-orange-700",
];

export function roleBadgeClass(roleName) {
  if (ROLE_BADGE_COLORS[roleName]) return ROLE_BADGE_COLORS[roleName];
  let hash = 0;
  for (let i = 0; i < roleName.length; i++) hash = (hash * 31 + roleName.charCodeAt(i)) >>> 0;
  return ROLE_FALLBACK_COLORS[hash % ROLE_FALLBACK_COLORS.length];
}

// What a role may hold: catalogue keys, never an admin-only operation (the server refuses both).
export function grantablePermissionKeys(catalog) {
  return new Set(catalog.filter((p) => !p.admin_only).map((p) => p.key));
}

// Keys stored on a role that it can no longer keep (removed from the catalogue, or admin-only), sorted. The admin role's "*"
// is not a key of the catalogue and is never reported.
export function obsoletePermissionKeys(permissions, catalog) {
  const grantable = grantablePermissionKeys(catalog);
  return [...permissions].filter((k) => k !== "*" && !grantable.has(k)).sort();
}

// The keys sent when a role is saved: the grantable ones only, in a stable order.
export function savablePermissionKeys(permissions, catalog) {
  const grantable = grantablePermissionKeys(catalog);
  return [...permissions].filter((k) => grantable.has(k)).sort();
}

// Rights that only make sense together: managing the organisation's referentials, assessing or accrediting someone is done
// from the collaborators directory, which needs « Voir les collaborateurs ». Shown as a warning, never added on its own
// (no permission is ever implied).
const PERMISSION_PREREQUISITES = {
  // Proposing an assistance to a pharmacy account is done from the technician's console.
  "support:initiate": ["support:handle"],
  // Reorganising the chart is carried by the right to read it (lot L10).
  "collaborators:reorganize": ["collaborators:view_org"],
  // Cartography: comparing and asking for an update are carried by the right to read it (lot CRM-4).
  "architecture:compare": ["architecture:view"],
  "architecture:generate": ["architecture:view"],
  "architecture:export": ["architecture:view"],
  "services:manage": ["collaborators:view"],
  "skills:manage": ["collaborators:view"],
  "skills:assess": ["collaborators:view"],
  "accreditations:grant": ["collaborators:view"],
  // Missions: acting on « all » missions is useless without seeing them; executing needs seeing one's missions.
  "missions:manage_all": ["missions:view_all"],
  "missions:execute": ["missions:view"],
  // Mission types are managed in Organisation, which lives in the collaborators' directory.
  "missions:manage_types": ["collaborators:view"],
  // Governance: every writing gesture is carried by the read gate too (Ordre 22, D-71).
  "knowledge:manage": ["knowledge:view"],
  "governance:manage_meetings": ["governance:view"],
  "governance:create_decision": ["governance:view"],
  "governance:approve_decision": ["governance:view"],
  "governance:manage_risks": ["governance:view"],
  "governance:export": ["governance:view"],
  "governance:view_all": ["governance:view"],
  // Administration Jamora (Ordre 27, D-30): every view key is useless without opening the application.
  "jamora_admin:view_platform": ["jamora_admin:access"],
  "jamora_admin:view_pharmacy": ["jamora_admin:access"],
  "jamora_admin:view_business_data": ["jamora_admin:access", "jamora_admin:view_pharmacy"],
  "jamora_admin:view_logs": ["jamora_admin:access"],
  "jamora_admin:export": ["jamora_admin:access", "jamora_admin:view_pharmacy", "jamora_admin:view_business_data"],
  // Cockpit: exporting needs opening it (Ordre 23, D-30).
  "analytics:export": ["analytics:view_company"],
  // Customer accounts: acting on an account always needs seeing it (Ordre 07, D-30).
  "customers:manage": ["customers:view"],
  "customers:manage_all": ["customers:view_all"],
  // Support tickets (Ordre 08): handling a ticket needs seeing it.
  "tickets:manage": ["tickets:view"],
  "tickets:manage_all": ["tickets:view_all"],
  // Revenue (Ordre 17, D-60): every write route also requires seeing the revenue data.
  "billing:manage_offers": ["billing:view"],
  "billing:manage_subscriptions": ["billing:view"],
  "billing:manage_invoices": ["billing:view"],
  "billing:record_payment": ["billing:view"],
  "billing:export": ["billing:view"],
  "records:view_confidential": ["records:view"],
  "records:create": ["records:view"],
  "records:approve": ["records:view"],
  "records:sign": ["records:view"],
  "records:export": ["records:view"],
  "records:manage_templates": ["records:view"],
  // Product (Ordre 19, D-50): every write route also requires seeing the product data. submit_feedback needs nothing else.
  "product:triage": ["product:view"],
  "product:manage_roadmap": ["product:view"],
  "product:manage_releases": ["product:view"],
  "product:manage_incidents": ["product:view"],
  // Purchases and assets (Ordre 20, D-60): every write route also requires seeing what it acts on.
  "procurement:manage": ["procurement:view"],
  "procurement:approve": ["procurement:view"],
  "assets:view_costs": ["assets:view"],
  "assets:manage": ["assets:view"],
  // The Copilote proposes actions inside a conversation: without « Utiliser le Copilote » there is no conversation.
  "assistant:act": ["assistant:use"],
};

// The prerequisites a role is missing for one of its keys (empty when the key is off or nothing is missing).
export function missingPrerequisites(key, permissions) {
  if (!permissions.has(key)) return [];
  return (PERMISSION_PREREQUISITES[key] || []).filter((needed) => !permissions.has(needed));
}
